import React, { type ReactNode } from 'react';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Stack } from '@cherre-frontend/ui';
import type { CustomPropertyAttribute } from '../../types/CustomPropertyAttribute';
import { CustomAttributeCell } from './CustomAttributeCell';

interface CustomAttributesListProps {
  disableReorder?: boolean;
  attributes: CustomPropertyAttribute[];
  renderActions?: (attribute: CustomPropertyAttribute) => ReactNode;
  onMoveAttribute: (fromAttributeSlug: string, toAttributeSlug: string) => void;
}

const cellHeight = 35.11;
const gapHeight = 8;
const calculateMinHeight = (count: number) =>
  count * (cellHeight + gapHeight) - gapHeight;

export const CustomAttributesList = ({
  attributes,
  disableReorder,
  renderActions,
  onMoveAttribute,
}: CustomAttributesListProps) => {
  const attributesSlugs = attributes.map(({ slug }) => slug);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const moveAttribute = ({ active, over }: DragEndEvent) => {
    const isString = (value: unknown): value is string =>
      typeof value === 'string';

    const overId = over?.id;
    if (isString(active.id) && isString(overId) && active.id !== overId) {
      onMoveAttribute(active.id, overId);
    }
  };

  const tenAttributesHeight = calculateMinHeight(10);
  const allAttributesHeight = calculateMinHeight(attributes.length);
  const minHeight = `min(${allAttributesHeight}px, ${tenAttributesHeight}px)`;

  return (
    <Stack
      component='ul'
      gap={1}
      margin={0}
      padding={0}
      minHeight={minHeight}
      sx={{ flex: '1 1 auto' }}
      overflow='hidden auto'
    >
      <DndContext
        sensors={sensors}
        onDragEnd={moveAttribute}
        collisionDetection={closestCenter}
        modifiers={[restrictToFirstScrollableAncestor]}
      >
        <SortableContext
          items={attributesSlugs}
          strategy={verticalListSortingStrategy}
        >
          {attributes.map((attribute) => (
            <Stack
              component='li'
              key={attribute.slug}
              direction='row'
              gap={1}
              alignItems='center'
            >
              <CustomAttributeCell
                slug={attribute.slug}
                label={attribute.label}
                type={attribute.type}
                required={attribute.required}
                disableReorder={disableReorder}
                isTargetPropertyType={attribute.isTargetPropertyType}
              />

              {renderActions ? renderActions(attribute) : null}
            </Stack>
          ))}
        </SortableContext>
      </DndContext>
    </Stack>
  );
};
